/**
 * API client para o app `producao/` do backend (Fase 8.1 — ADR-0022).
 *
 * Endpoints (sem trailing slash):
 *   GET  /api/ordens-producao                    list (filtros)
 *   GET  /api/ordens-producao/{id}               retrieve
 *   POST /api/ordens-producao/{id}/cancelar      body: {motivo}
 *   POST /api/ordens-producao/{id}/liberar       placeholder F8.4
 *
 * Criacao de OPs eh via action `gerar-ops` no PedidoViewSet
 * (services/pedidos.js -> gerarOpsDoPedido).
 *
 * Filtros aceitos no list:
 *   pedido (uuid)
 *   situacao (RASCUNHO | LIBERADA | EM_PRODUCAO | CONCLUIDA | CANCELADA)
 *   estacao_atual (CORTE | COLADEIRA | FURACAO | MONTAGEM | ACABAMENTO | EMBALAGEM)
 *   unidade (uuid)
 *   busca (string — numero, descricao, pedido__numero)
 *   page, page_size
 */

import { ACCESS_API_URL } from "../config/api";

const PREFIXO_OP = "/api/ordens-producao";

export function listarOrdensProducao(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.busca) usp.set("busca", params.busca);
  if (params.situacao) usp.set("situacao", params.situacao);
  if (params.estacao_atual) usp.set("estacao_atual", params.estacao_atual);
  if (params.pedido) usp.set("pedido", params.pedido);
  // Producao v2 (P3): pecas de um ambiente/lote sem N chamadas.
  if (params.ambienteProducao) {
    usp.set("ambiente_producao", params.ambienteProducao);
  }
  if (params.lote) usp.set("lote", params.lote);
  if (params.unidade) usp.set("unidade", params.unidade);
  if (params.page) usp.set("page", params.page);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREFIXO_OP}${qs ? `?${qs}` : ""}`);
}

export function obterOrdemProducao(request, id) {
  return request(`${PREFIXO_OP}/${id}`);
}

export function cancelarOp(
  request, id, motivo, { m2_aproveitavel, observacao_aproveitamento } = {},
) {
  // F8.5 (ADR-0013): aceita m2_aproveitavel opcional. Quando > 0, backend
  // cria ChapaAproveitamento vinculado a OP (sobra catalogada).
  const body = { motivo };
  if (m2_aproveitavel !== undefined && m2_aproveitavel !== null) {
    body.m2_aproveitavel = String(m2_aproveitavel);
  }
  if (observacao_aproveitamento) {
    body.observacao_aproveitamento = observacao_aproveitamento;
  }
  return request(`${PREFIXO_OP}/${id}/cancelar`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function liberarOp(request, id) {
  return request(`${PREFIXO_OP}/${id}/liberar`, { method: "POST" });
}

// ---------- F8.2 (ADR-0023) — eventos de bipagem ----------

const PREFIXO_EVENTOS = "/api/manufacturing-events";

export function listarEventosBipagem(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.op) usp.set("op", params.op);
  if (params.operador) usp.set("operador", params.operador);
  if (params.estacao) usp.set("estacao", params.estacao);
  if (params.desde) usp.set("desde", params.desde);
  const qs = usp.toString();
  return request(`${PREFIXO_EVENTOS}${qs ? `?${qs}` : ""}`);
}

export const ACAO_BIPAGEM_LABELS = {
  INICIAR: "Iniciou",
  CONCLUIR: "Concluiu",
};

export const ACAO_BIPAGEM_TONES = {
  INICIAR: "info",
  CONCLUIR: "success",
};

// ---------- F8.2 — Operadores PIN ----------

const PREFIXO_OPERADORES = "/api/operadores";

export function listarOperadores(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.unidade) usp.set("unidade", params.unidade);
  if (params.ativo !== undefined) usp.set("ativo", params.ativo);
  if (params.bloqueados) usp.set("bloqueados", "true");
  const qs = usp.toString();
  return request(`${PREFIXO_OPERADORES}${qs ? `?${qs}` : ""}`);
}

export function criarOperador(request, dados) {
  return request(PREFIXO_OPERADORES, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarOperador(request, id, dados) {
  return request(`${PREFIXO_OPERADORES}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function desativarOperador(request, id) {
  return request(`${PREFIXO_OPERADORES}/${id}`, {
    method: "DELETE",
  });
}

export function resetarPinOperador(request, id) {
  return request(`${PREFIXO_OPERADORES}/${id}/resetar-pin`, {
    method: "POST",
  });
}

export function desbloquearOperador(request, id) {
  return request(`${PREFIXO_OPERADORES}/${id}/desbloquear`, {
    method: "POST",
  });
}

// ---------- Labels ----------

export const SITUACAO_LABELS = {
  RASCUNHO: "Rascunho",
  LIBERADA: "Liberada",
  EM_PRODUCAO: "Em produção",
  CONCLUIDA: "Concluida",
  CANCELADA: "Cancelada",
};

export const SITUACAO_TONES = {
  RASCUNHO: "neutral",
  LIBERADA: "info",
  EM_PRODUCAO: "warning",
  CONCLUIDA: "success",
  CANCELADA: "danger",
};

export const ESTACOES_LABELS = {
  CORTE: "Corte",
  COLADEIRA: "Coladeira",
  FURACAO: "Furacao",
  MONTAGEM: "Montagem",
  ACABAMENTO: "Acabamento",
  EMBALAGEM: "Embalagem",
};

// ---------- F8.3 (ADR-0014) — Etiquetagem Code 128 ----------
//
// Helpers retornam URLs prontas para `window.open(url, "_blank")` —
// o browser abre o PDF em nova aba e o usuario imprime/baixa.
// Backend requer JWT no header, entao o fetch da view normal NAO
// funciona aqui (browser abre sem header). Estrategia:
//   1. Usuario clica botao -> chamamos endpoint via `request()` que
//      fetch com header Authorization, recebe blob, gera URL temp.
//   2. window.open dessa URL temporaria.
//
// Para reimpressao com motivo, o caller monta a query `?motivo=...`.

export async function abrirEtiquetaOpEmNovaAba(request, opId, { motivo } = {}) {
  const usp = new URLSearchParams();
  if (motivo) usp.set("motivo", motivo);
  const qs = usp.toString();
  const url = `/api/ordens-producao/${opId}/etiqueta${qs ? `?${qs}` : ""}`;
  return _fetchEAbrirPdf(request, url, "GET", null, `etiqueta-${opId}.pdf`);
}

export async function imprimirEtiquetasOpLote(
  request, { opIds, motivo = "" } = {},
) {
  return _fetchEAbrirPdf(
    request,
    "/api/ordens-producao/imprimir-etiquetas",
    "POST",
    { op_ids: opIds, motivo },
    `etiquetas-ops-${opIds.length}.pdf`,
  );
}

export async function abrirEtiquetaExpedicaoEmNovaAba(
  request, pedidoId, { ambienteId, motivo } = {},
) {
  const usp = new URLSearchParams();
  if (ambienteId) usp.set("ambiente_id", ambienteId);
  if (motivo) usp.set("motivo", motivo);
  const qs = usp.toString();
  const url = `/api/pedidos/${pedidoId}/etiqueta-expedicao${qs ? `?${qs}` : ""}`;
  return _fetchEAbrirPdf(
    request, url, "GET", null, `expedicao-${pedidoId}.pdf`,
  );
}

/**
 * Helper interno: usa `request()` (que injeta Authorization) para
 * pegar binario PDF + abre em nova aba via Blob URL temporaria.
 *
 * Como o `request()` do AuthContext faz JSON parse, vou usar um
 * fetch direto com headers do AuthContext via metodo low-level.
 * Aqui fazemos via fetch nativo com `ACCESS_API_URL` + token manual.
 */
async function _fetchEAbrirPdf(request, endpoint, method, body, filename) {
  // Aproveita o `request` do AuthContext mas pede raw blob.
  // O `request()` ja sabe lidar com Authorization + refresh; vamos
  // passar uma opcao especial `__rawBlob = true` que ele respeita.
  //
  // Como nao vamos mudar AuthContext agora, fazemos fetch direto e
  // copiamos o pattern de Authorization do session storage.
  const apiUrl = ACCESS_API_URL;
  const session = JSON.parse(
    window.localStorage.getItem("pharuerp:access-session") || "{}",
  );
  // Security Sprint 1 (A16, audit 2026-05-21): session usa `accessToken`
  // direto (ver AuthContext.jsx). Path antigo `session.tokens.access`
  // sempre era `undefined` -> request saia sem Authorization -> 401.
  // Etiquetas Code 128 voltam a funcionar com este fix.
  const accessToken = session?.accessToken;

  const headers = { "Content-Type": "application/json" };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  const init = { method, headers };
  if (body !== null && body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const resp = await fetch(`${apiUrl}${endpoint}`, init);
  if (!resp.ok) {
    let detalhe = `Erro ${resp.status} ao gerar etiqueta.`;
    let reimpressao = false;
    try {
      const data = await resp.json();
      detalhe = data?.detalhe || data?.detail || detalhe;
      reimpressao = data?.reimpressao || false;
    } catch {
      /* body nao eh json — segue com mensagem padrao */
    }
    const err = new Error(detalhe);
    err.status = resp.status;
    err.reimpressao = reimpressao;
    throw err;
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  // Abre em nova aba — usuario clica imprimir no PDF viewer
  const win = window.open(url, "_blank");
  if (!win) {
    // Popup bloqueado — faz download direto
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
  }
  // Libera URL apos 60s pra navegador conseguir carregar
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return { ok: true };
}
